import TableOnly from "components/DataTable/TableOnly";
import DeleteModalButton from "components/DeleteModalBtn/DeleteModalButton";
import Pagination from "components/Pagination/Pagination";
import SearchComp from "components/SearchComp/SearchComp";
import Spinner from "components/Spinner";
import { setNotification } from "features/Notification/notificationSlice";
import { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import { useDispatch } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import authHeader from "services/auth-header";
import API from "services/axios";

/**
 * Sales -> Utilities -> Products -> Workflow (user, 2026-10-29). One
 * workflow per product type (Screen Printing, Embroidery ...; per product
 * style for a day, back to type 2026-10-30, user); Details opens the
 * React Flow builder, where its steps are drawn, each at a station. Same
 * list layout as the other Products tabs (Show, Search, Add button + modal,
 * table, pages).
 */

const COMPONENT_URL = "/sales/products";
const limits = [5, 10, 15, 20];

// "10/29 - 9:23 PM", like the other lists.
const shortDate = (value) => {
  if (!value) return "";
  const d = new Date(value);
  const time = d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  return `${d.getMonth() + 1}/${d.getDate()} - ${time}`;
};

export default function SWorkflows() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [loading, setLoading] = useState(false);
  const [workflows, setWorkflows] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [types, setTypes] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [isUpdateMode, setIsUpdateMode] = useState(false);
  const [formData, setFormData] = useState({});

  const notify = (message, type = "success") => dispatch(setNotification({ message, type }));

  const changeLimit = (val) =>
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      newParams.set("limit", val);
      newParams.delete("page");
      return newParams.toString();
    });

  const getSearchParamsWithoutGivenKey = (key) => {
    const params = new URLSearchParams(searchParams);
    params.delete(key);
    return params.toString();
  };

  const getWorkflows = async () => {
    try {
      setLoading(true);
      const body = {
        page: parseInt(searchParams.get("page")) || 1,
        limit: parseInt(searchParams.get("limit")) || 10,
        query: searchParams.get("query") || "",
        sort: searchParams.get("sort") || "productTypeName",
        order: searchParams.get("order") || "asc",
      };
      const { data } = await API.post("/sales/workflows", body, { headers: authHeader() });
      if (!data?.success) throw new Error(data?.message || "Failed");
      setWorkflows(data.data || []);
      setTotalPages(data.totalPages || 1);
    } catch (err) {
      notify(err?.response?.data?.message || err.message, "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timeout = setTimeout(getWorkflows, 50);
    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // The product types for the modal (each says whether it already has a
  // workflow). One workflow per product type (user, 2026-10-30).
  const loadTypes = async () => {
    try {
      const { data } = await API.get("/sales/workflow-types", { headers: authHeader() });
      setTypes(data?.data || []);
    } catch (err) {
      notify(err?.response?.data?.message || err.message, "error");
    }
  };

  // Types without a workflow (plus the one being edited): a type that has
  // one is not offered again (user, 2026-11-02).
  const typeChoices = types.filter((t) => !t.workflowId || t.workflowId === formData.id);

  const openAdd = () => {
    setFormData({ productTypeId: "" });
    setIsUpdateMode(false);
    setShowAddModal(true);
    loadTypes();
  };

  const openUpdate = (item) => {
    setFormData({ id: item.id, productTypeId: String(item.productTypeId) });
    setIsUpdateMode(true);
    setShowAddModal(true);
    loadTypes();
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      // No name (user, 2026-11-02): one workflow per type, so the type names it.
      const body = { productTypeId: formData.productTypeId };
      const { data } = isUpdateMode
        ? await API.patch("/sales/workflow", { ...body, id: formData.id }, { headers: authHeader() })
        : await API.post("/sales/workflow", body, { headers: authHeader() });
      if (!data?.success) throw new Error(data?.message || "Failed");
      notify(data.message);
      setShowAddModal(false);
      if (!isUpdateMode && data?.data?.id) {
        navigate(`/sales/products/workflow/${data.data.id}`);
        return;
      }
      await getWorkflows();
    } catch (err) {
      notify(err?.response?.data?.message || err.message, "error");
    } finally {
      setLoading(false);
    }
  };

  const deleteWorkflow = async (id) => {
    try {
      setLoading(true);
      const { data } = await API.delete(`/sales/workflow/${id}`, { headers: authHeader() });
      if (!data?.success) throw new Error(data?.message || "Failed");
      notify(data.message);
      await getWorkflows();
    } catch (err) {
      notify(err?.response?.data?.message || err.message, "error");
    } finally {
      setLoading(false);
    }
  };

  /*   Table functions
   ********************************************* */
  const table_head = [
    { name: "ID", value: "id" },
    { name: "Product Type", value: "productTypeName" },
    { name: "Steps", value: "steps" },
    { name: "Stations", value: "stations" },
    { name: "Updated", value: "updatedAt" },
    { name: "Actions", value: "Actions" },
  ];

  const table_body = (item) => (
    <tr key={item.id}>
      <td className="py-0 align-middle">{item.id}</td>
      <td className="py-0 align-middle">
        {item.productTypeName || <span className="text-danger">Product type removed</span>}
      </td>
      <td className="py-0 align-middle">{item.steps}</td>
      <td className="py-0 align-middle">{item.stations}</td>
      <td className="py-0 align-middle">{shortDate(item.updatedAt)}</td>
      <td className="py-0 align-middle table-action text-nowrap">
        <button
          className="btn btn-success btn-sm me-1"
          onClick={() => navigate(`/sales/products/workflow/${item.id}`)}
        >
          Details
        </button>
        <button disabled={loading} onClick={() => openUpdate(item)} className="btn px-1">
          <i className="mdi mdi-pencil"></i>
        </button>
        <DeleteModalButton
          btnClass={"btn  px-1"}
          deleteFunc={deleteWorkflow}
          deleteParams={item.id}
          disableConditions={loading}
        />
      </td>
    </tr>
  );

  return (
    <div className="p-2" style={{ height: "90vh" }}>
      {loading && <Spinner />}
      <div className="px-2">
        <div className="d-flex justify-content-end align-items-center gap-1 mb-3">
          <div className="flex-grow-1 d-flex justify-content-between align-items-center">
            <div className="d-flex align-items-center gap-1">
              <div>Show</div>
              <button
                className="px-1 bg-primary bg-opacity-10 border border-primary rounded-end rounded-start "
                data-bs-toggle="dropdown"
                aria-expanded="false"
              >
                {searchParams.get("limit") || 10}
              </button>
              <ul style={{ width: "fit-content" }} className="dropdown-menu">
                {limits.map((limit) => (
                  <li key={limit}>
                    <button className="dropdown-item" onClick={() => changeLimit(limit)}>
                      {limit}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <SearchComp
            setLoading={setLoading}
            apiPath={`${COMPONENT_URL}${
              getSearchParamsWithoutGivenKey("query") ? "?" + getSearchParamsWithoutGivenKey("query") + "&" : "?"
            }`}
          />
          <div>
            <button onClick={openAdd} type="button" className="btn btn-primary ms-1">
              Add Workflow
            </button>
          </div>
        </div>
        <div className="mb-3" style={{ height: "75vh", overflowY: "auto", borderRadius: 15 }}>
          <TableOnly
            table_head={table_head}
            table_body={table_body}
            table_data={workflows}
            totalPages={totalPages}
          />
        </div>
        <Pagination
          totalPages={totalPages}
          apiPath={`${COMPONENT_URL}${
            searchParams.toString() ? "?" + getSearchParamsWithoutGivenKey("page") + "&" : "?"
          }page=`}
          currPage={parseInt(searchParams.get("page")) || 1}
        />
      </div>

      {/*   ADD / UPDATE Modal
       ********************************************* */}
      <Modal show={showAddModal} onHide={() => setShowAddModal(false)}>
        <Modal.Body className="shadow-lg bg-white px-3 pb-4" style={{ borderRadius: 15 }}>
          <form onSubmit={submit}>
            <div className="d-flex justify-content-between align-items-center mb-2">
              <h4>{isUpdateMode ? "Update Workflow" : "Add Workflow"}</h4>
              <div className="d-flex justify-content-end">
                <button onClick={() => setShowAddModal(false)} className="btn btn-secondary btn-sm" type="button">
                  Close
                </button>
                <button
                  disabled={loading || !formData.productTypeId}
                  className="btn btn-primary btn-sm ms-1"
                  type="submit"
                >
                  {isUpdateMode ? "Update" : "Add"}
                </button>
              </div>
            </div>
            <hr className="mt-0" />
            <div className="my-3">
              <label className="form-label">Product Type</label>
              <select
                className="form-select"
                autoFocus
                required
                value={formData.productTypeId || ""}
                onChange={(e) => setFormData((f) => ({ ...f, productTypeId: e.target.value }))}
              >
                <option value="">Choose a product type...</option>
                {typeChoices.map((t) => (
                  <option key={t.productTypeId} value={t.productTypeId}>
                    {t.productTypeName}
                  </option>
                ))}
              </select>
              {!isUpdateMode && types.length > 0 && typeChoices.length === 0 && (
                <small className="text-muted">Every product type already has a workflow.</small>
              )}
            </div>
          </form>
        </Modal.Body>
      </Modal>
    </div>
  );
}
